import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { ApiError, api } from "../api/client";
import { Badge, Card, EmptyState, Skeleton, TableSkeleton, cx } from "./ui";
import { formatDate, formatDateTime } from "../lib/format";
import { ATTENDANCE_LABEL, ATTENDANCE_TONE, formatMinutes } from "../lib/people";
import type { Attendance, AttendanceStatus } from "../api/types";

type View = "today" | "days" | "month";

/**
 * One attendance surface, reused by the Attendance module and the Employee
 * Profile's Attendance tab. Grouping is by the row's explicit ``work_date``, so
 * an overnight shift stays on the day it was rostered for.
 */
export function AttendanceDetail({
  employeeId,
  timezone,
}: {
  employeeId: string;
  timezone?: string | null;
}) {
  const [view, setView] = useState<View>("today");
  const today = useMemo(() => localDate(timezone), [timezone]);

  return (
    <div>
      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {(
          [
            ["today", "Today"],
            ["days", "15 Days"],
            ["month", "Month"],
          ] as Array<[View, string]>
        ).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setView(value)}
            className={cx(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
              view === value
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {view === "today" ? (
        <TodayView employeeId={employeeId} today={today} timezone={timezone} />
      ) : null}
      {view === "days" ? <DaysView employeeId={employeeId} today={today} timezone={timezone} /> : null}
      {view === "month" ? <MonthView employeeId={employeeId} today={today} timezone={timezone} /> : null}
    </div>
  );
}

function TodayView({
  employeeId,
  today,
  timezone,
}: {
  employeeId: string;
  today: string;
  timezone?: string | null;
}) {
  const query = useQuery({
    queryKey: ["emp-attendance-today", employeeId, today],
    queryFn: ({ signal }) =>
      api.attendancePage({ employee: employeeId, date: today, page_size: 1 }, signal),
  });
  if (query.isPending) return <Skeleton className="h-24 w-full" />;
  if (query.isError) return <ErrorState error={query.error} />;
  const row = query.data?.results[0];
  if (!row) {
    return <EmptyState title="No attendance today" body="Nothing has been recorded for this employee today." />;
  }
  return <AttendanceCard row={row} timezone={timezone} />;
}

function DaysView({
  employeeId,
  today,
  timezone,
}: {
  employeeId: string;
  today: string;
  timezone?: string | null;
}) {
  const from = addDays(today, -14);
  const query = useQuery({
    queryKey: ["emp-attendance-days", employeeId, from, today],
    queryFn: ({ signal }) =>
      api.attendancePage({ employee: employeeId, from, to: today, page_size: 30 }, signal),
  });
  if (query.isPending) return <div className="overflow-hidden rounded-lg border border-slate-200"><TableSkeleton rows={6} /></div>;
  if (query.isError) return <ErrorState error={query.error} />;
  const rows = query.data?.results ?? [];
  const byDate = new Map(rows.map((row) => [row.work_date, row]));
  // Always 15 calendar days, newest first, with missing days shown rather than
  // silently omitted.
  const dates = Array.from({ length: 15 }, (_, index) => addDays(today, -index));
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5">Date</th>
            <th className="px-4 py-2.5">Shift</th>
            <th className="px-4 py-2.5">In</th>
            <th className="px-4 py-2.5">Out</th>
            <th className="px-4 py-2.5">Worked</th>
            <th className="px-4 py-2.5">Late</th>
            <th className="px-4 py-2.5">OT</th>
            <th className="px-4 py-2.5">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {dates.map((date) => {
            const row = byDate.get(date);
            if (!row) {
              return (
                <tr key={date} className="text-slate-400">
                  <td className="px-4 py-2.5 text-sm">{formatDate(date, timezone ?? undefined)}</td>
                  <td className="px-4 py-2.5 text-sm" colSpan={6}>
                    No attendance record
                  </td>
                  <td className="px-4 py-2.5 text-sm">—</td>
                </tr>
              );
            }
            return (
              <tr key={row.id} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 text-sm text-slate-700">{formatDate(row.work_date, timezone ?? undefined)}</td>
                <td className="px-4 py-2.5 text-sm text-slate-500">{shiftLabel(row)}</td>
                <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">{formatDateTime(row.clock_in_at, timezone ?? undefined)}</td>
                <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">{formatDateTime(row.clock_out_at, timezone ?? undefined)}</td>
                <td className="px-4 py-2.5 text-sm tabular-nums text-slate-700">{formatMinutes(row.worked_minutes)}</td>
                <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">{row.late_minutes ? `${row.late_minutes}m` : "—"}</td>
                <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">{row.overtime_minutes ? `${row.overtime_minutes}m` : "—"}</td>
                <td className="px-4 py-2.5">
                  <ExceptionOrStatus row={row} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MonthView({
  employeeId,
  today,
  timezone,
}: {
  employeeId: string;
  today: string;
  timezone?: string | null;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [month, setMonth] = useState(today.slice(0, 7));
  const first = `${month}-01`;
  const last = endOfMonth(first);
  const query = useQuery({
    queryKey: ["emp-attendance-month", employeeId, first, last],
    queryFn: ({ signal }) =>
      api.attendancePage({ employee: employeeId, from: first, to: last, page_size: 40 }, signal),
  });

  if (query.isPending) return <Skeleton className="h-48 w-full" />;
  if (query.isError) return <ErrorState error={query.error} />;

  const rows = query.data?.results ?? [];
  const byDate = new Map(rows.map((row) => [row.work_date, row]));
  const cells = monthCells(first);
  const selectedRow = selected ? byDate.get(selected) : undefined;

  const summary = {
    present: rows.filter((r) => r.status === "present").length,
    absent: rows.filter((r) => r.status === "absent").length,
    half: rows.filter((r) => r.status === "half_day").length,
    leave: rows.filter((r) => r.status === "on_leave").length,
    weekOff: rows.filter((r) => r.status === "week_off").length,
    holiday: rows.filter((r) => r.status === "holiday").length,
    hours: rows.reduce((sum, r) => sum + (r.worked_minutes ?? 0), 0),
    overtime: rows.reduce((sum, r) => sum + (r.overtime_minutes ?? 0), 0),
    late: rows.filter((r) => (r.late_minutes ?? 0) > 0).length,
    exceptions: rows.filter((r) => r.is_exception).length,
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <div className="mb-2 flex items-center justify-between">
          <button
            onClick={() => setMonth(addMonths(month, -1))}
            className="rounded-md px-2 py-1 text-sm text-slate-600 hover:bg-slate-100"
            aria-label="Previous month"
          >
            ← Prev
          </button>
          <span className="text-sm font-medium text-slate-900">{monthLabel(month)}</span>
          <button
            onClick={() => setMonth(addMonths(month, 1))}
            className="rounded-md px-2 py-1 text-sm text-slate-600 hover:bg-slate-100"
            aria-label="Next month"
          >
            Next →
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div key={day} className="pb-1 text-center text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {day}
            </div>
          ))}
          {cells.map((cell, index) =>
            cell === null ? (
              <div key={`blank-${index}`} />
            ) : (
              <button
                key={cell}
                onClick={() => setSelected(cell)}
                className={cx(
                  "aspect-square rounded-md text-xs font-medium tabular-nums ring-1 ring-inset transition",
                  cellStatusClass(byDate.get(cell)?.status),
                  selected === cell ? "ring-2 ring-slate-900" : "ring-transparent hover:ring-slate-300",
                )}
                title={`${cell} · ${byDate.get(cell) ? ATTENDANCE_LABEL[byDate.get(cell)!.status ?? "present"] : "No record"}`}
              >
                <span className="block pt-1.5">{Number(cell.slice(-2))}</span>
              </button>
            ),
          )}
        </div>
      </div>

      <div className="space-y-4">
        <Card padded={false} className="p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">This month</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
            <Stat label="Present" value={summary.present} />
            <Stat label="Absent" value={summary.absent} />
            <Stat label="Half day" value={summary.half} />
            <Stat label="Leave" value={summary.leave} />
            <Stat label="Weekly off" value={summary.weekOff} />
            <Stat label="Holiday" value={summary.holiday} />
            <Stat label="Late days" value={summary.late} />
            <Stat label="Exceptions" value={summary.exceptions} />
            <Stat label="OT" value={formatMinutes(summary.overtime)} />
            <dt className="col-span-2 mt-1 border-t border-slate-100 pt-1.5 text-slate-500">Total hours</dt>
            <dd className="col-span-2 font-medium tabular-nums text-slate-900">{formatMinutes(summary.hours)}</dd>
          </dl>
        </Card>

        {selected ? (
          selectedRow ? (
            <AttendanceCard row={selectedRow} timezone={timezone} />
          ) : (
            <Card>
              <p className="text-sm text-slate-500">
                {formatDate(selected, timezone ?? undefined)} has no attendance record.
              </p>
            </Card>
          )
        ) : (
          <p className="text-xs text-slate-400">Select a date to see its details.</p>
        )}
      </div>
    </div>
  );
}

function AttendanceCard({ row, timezone }: { row: Attendance; timezone?: string | null }) {
  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{formatDate(row.work_date, timezone ?? undefined)}</h3>
        <ExceptionOrStatus row={row} />
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Field label="Shift" value={shiftLabel(row)} />
        <Field label="Expected" value={row.expected_minutes ? formatMinutes(row.expected_minutes) : "—"} />
        <Field label="Check-in" value={formatDateTime(row.clock_in_at, timezone ?? undefined)} />
        <Field label="Check-out" value={formatDateTime(row.clock_out_at, timezone ?? undefined)} />
        <Field label="Worked" value={formatMinutes(row.worked_minutes)} />
        <Field label="Late" value={row.late_minutes ? `${row.late_minutes}m` : "—"} />
        <Field label="Overtime" value={row.overtime_minutes ? `${row.overtime_minutes}m` : "—"} />
        <Field label="Source" value={row.source?.replace(/_/g, " ")} />
        <Field label="Location" value={locationText(row)} />
        <Field label="IP address" value={row.clock_in_ip || "Not provided"} />
      </dl>
      <p className="mt-3 text-xs text-slate-400">
        Location is reported by the employee's device at check-in; the IP is recorded
        by the server. Both are shown as recorded, not verified.
      </p>
      {row.notes ? (
        <p className="mt-3 border-t border-slate-100 pt-3 text-sm text-slate-600">{row.notes}</p>
      ) : null}
    </Card>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-slate-800">{value || "—"}</dd>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium tabular-nums text-slate-900">{value}</dd>
    </>
  );
}

function ExceptionOrStatus({ row }: { row: Attendance }) {
  // A missing punch is an exception the manager must see, even though the
  // stored status is still "present" -- the day is not complete.
  if (row.missing_check_in) return <Badge tone="amber">Missing check-in</Badge>;
  if (row.missing_check_out) return <Badge tone="rose">Missing check-out</Badge>;
  const status = row.status ?? "present";
  return <Badge tone={ATTENDANCE_TONE[status] ?? "slate"}>{ATTENDANCE_LABEL[status] ?? status}</Badge>;
}

function shiftLabel(row: Attendance): string {
  if (!row.scheduled_start_time || !row.scheduled_end_time) return "Unscheduled";
  return `${row.scheduled_start_time.slice(0, 5)}–${row.scheduled_end_time.slice(0, 5)}`;
}

/** The device's reported position, or a clear "not provided" state. */
function locationText(row: Attendance): string {
  if (!row.has_location) return "Not provided";
  const latitude = Number(row.clock_in_latitude);
  const longitude = Number(row.clock_in_longitude);
  const accuracy =
    row.clock_in_accuracy_m != null ? ` (±${Math.round(row.clock_in_accuracy_m)} m)` : "";
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}${accuracy}`;
}

function cellStatusClass(status: AttendanceStatus | undefined): string {
  switch (status) {
    case "present":
      return "bg-emerald-100 text-emerald-800";
    case "half_day":
      return "bg-amber-100 text-amber-800";
    case "on_leave":
      return "bg-sky-100 text-sky-800";
    case "absent":
      return "bg-rose-100 text-rose-800";
    case "holiday":
      return "bg-violet-100 text-violet-800";
    case "week_off":
      return "bg-slate-100 text-slate-500";
    default:
      return "bg-white text-slate-400";
  }
}

function ErrorState({ error }: { error: unknown }) {
  return (
    <EmptyState
      title="Could not load attendance"
      body={error instanceof ApiError ? error.message : "Please try again."}
    />
  );
}

function localDate(timeZone?: string | null): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone ?? undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  return `${map.year}-${map.month}-${map.day}`;
}

function addDays(iso: string, days: number): string {
  const value = new Date(`${iso}T00:00:00`);
  value.setDate(value.getDate() + days);
  return toIso(value);
}

function endOfMonth(iso: string): string {
  const value = new Date(`${iso}T00:00:00`);
  value.setMonth(value.getMonth() + 1, 0);
  return toIso(value);
}

function addMonths(month: string, delta: number): string {
  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(5, 7));
  const value = new Date(year, monthIndex - 1 + delta, 1);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(month: string): string {
  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(5, 7));
  return new Date(year, monthIndex - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

/** Leading blanks to align day 1 under its weekday (Monday-first), then days. */
function monthCells(firstIso: string): Array<string | null> {
  const first = new Date(`${firstIso}T00:00:00`);
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const offset = (first.getDay() + 6) % 7;
  const cells: Array<string | null> = Array.from({ length: offset }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    const value = new Date(first.getFullYear(), first.getMonth(), day);
    cells.push(toIso(value));
  }
  return cells;
}

function toIso(value: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  return `${map.year}-${map.month}-${map.day}`;
}
